import { Controller } from "@hotwired/stimulus"

// CLA-83: pure client-side DOM repair, sanctioned by design D2's Risks
// section as the fallback for this exact failure mode.
//
// #classroom_lesson_nav lives outside <turbo-frame id="lesson_viewer"> (D2),
// so lesson switches keep it in sync via an OOB turbo_stream embedded in the
// frame response, while data-turbo-action="advance" drives page-level
// history. What's established: a frame-driven "advance" visit can leave a
// browser Back restoring a page whose sidebar highlight disagrees with the
// URL it's keyed under, and a Turbo "restore" cache hit issues no
// reconciling request, so the stale state never self-corrects. The precise
// path inside Turbo's snapshot/cache bookkeeping that produces this was not
// isolated -- that's exactly why this fix doesn't depend on it.
//
// Instead, this controller re-derives the active item from the current
// URL's lesson_id after every navigation. #classroom_lesson_nav is always
// freshly inserted (OOB replace on a frame switch, full render on a page
// visit) so connect() alone covers it; turbo:load/turbo:morph are kept as
// the same belt-and-braces pair used by call_button_controller.js for this
// bug class, in case some path here morphs it in place instead of
// replacing it.
//
// The active state itself (background, text color, dot, the nested parts
// list) is owned by CSS via the `aria-current` variant on the nav item
// partials -- this controller only toggles the `aria-current` attribute
// (page on the lesson link, step on the part link), never a class list, so a restyle of
// the partial can't silently desync from what this controller applies.
export default class extends Controller {
  connect() {
    this.sync()

    this.boundSync = this.sync.bind(this)
    document.addEventListener("turbo:load", this.boundSync)
    document.addEventListener("turbo:morph", this.boundSync)
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.boundSync)
    document.removeEventListener("turbo:morph", this.boundSync)
  }

  sync() {
    const params = new URLSearchParams(window.location.search)
    const lessonId = params.get("lesson_id")
    const sectionId = params.get("section_id")

    this.element.querySelectorAll("[data-lesson-id]").forEach((item) => {
      const link = item.querySelector("a")
      if (!link) return

      const current = item.dataset.lessonId === lessonId
      if (current) {
        link.setAttribute("aria-current", "page")
      } else {
        link.removeAttribute("aria-current")
      }

      // Only the current lesson gets a "step" part: the one named by section_id, else the first. The nested list's visibility is CSS (peer-aria-[current=page]) and follows the lesson link.
      const parts = item.querySelectorAll("[data-section-id]")
      const target = current
        ? (sectionId && item.querySelector(`[data-section-id="${CSS.escape(sectionId)}"]`)) || parts[0]
        : null
      parts.forEach((part) => {
        if (part === target) {
          part.setAttribute("aria-current", "step")
        } else {
          part.removeAttribute("aria-current")
        }
      })
    })
  }
}
